/**
 * The reason why this store exists is because multiple select must work across multiple
 * song lists, therefore it has to be centralized in a singleton that oversights all of them
 * and it's at the same time observable to make templates react.
 *
 * When TrackLists are rendered they call `addItems` to append the items. It relies on tracklists being
 * rendered in the order they are displayed, but I believe that svelte guarantees that, so that should not be a problem
 * unless there's some CSS inverting the rendering flow.
 */
import { getContext } from 'svelte';
import { get, writable } from 'svelte/store';
import {
    SelectionDirection,
    type ItemSelector,
    type ItemSelectorItem,
    type ItemSelectorState,
} from './types';

function createItemSelector<T extends ItemSelectorItem>(): ItemSelector<T> {
    const itemSelectorState = writable<ItemSelectorState<T>>({
        itemLists: new Map<Symbol, T[]>(),
        allItems: [],
        currentMouseTarget: null,
        selectedItemsWithShiftArrows: [],
        selectedItems: [],
        shiftArrowDirection: undefined,
        lastSelectedItemNotByShiftClick: undefined,
    });
    const { subscribe, set, update } = itemSelectorState;

    function flattenItemLists(itemLists: Map<Symbol, T[]>): T[] {
        let combinedList = [];

        // Combine the list items to one list. Using forEach because itemLists is a map so we can't use reduce.
        itemLists.forEach((items) => {
            combinedList = [...combinedList, ...items];
        });

        return combinedList;
    }

    function clearArrowMemory(
        state: ItemSelectorState<T>,
    ): ItemSelectorState<T> {
        return {
            ...state,
            shiftArrowDirection: undefined,
            selectedItemsWithShiftArrows: [],
        };
    }

    function expandShiftArrowSelection(state: ItemSelectorState<T>): void {
        const allItems = state.allItems;
        let currentIndex = allItems.findIndex(
            (track) => track === state.lastSelectedItemNotByShiftClick,
        );
        let adjacentTrack = allItems[currentIndex + state.shiftArrowDirection];
        if (!adjacentTrack) return;
        state.selectedItems = itemsInOriginalOrder(
            [...state.selectedItems, adjacentTrack],
            allItems,
        );
        state.lastSelectedItemNotByShiftClick = adjacentTrack;
        state.selectedItemsWithShiftArrows.push(adjacentTrack);
    }

    function contractShiftArrowSelection(state: ItemSelectorState<T>): void {
        const allItems = state.allItems;
        let popped = state.selectedItemsWithShiftArrows.pop();
        let previousSelected =
            state.selectedItemsWithShiftArrows[
                state.selectedItemsWithShiftArrows.length - 1
            ];
        if (previousSelected) {
            state.lastSelectedItemNotByShiftClick = previousSelected;
        } else {
            let currentIndex = allItems.findIndex(
                (track) => track === state.lastSelectedItemNotByShiftClick,
            );
            state.lastSelectedItemNotByShiftClick =
                allItems[currentIndex - state.shiftArrowDirection];
            state.shiftArrowDirection = undefined;
        }
        state.selectedItems = state.selectedItems.filter((t) => t !== popped);
    }

    // Every time the list of selected items is updated in a way that might have messed with the order
    // of the items we must ensure they are sorted respecting the position they have in the original list of items.
    // This is not necessary when narrowing the selection with `filter` or when there's only a single item.
    function itemsInOriginalOrder(selectedItems: T[], allItems: T[]): T[] {
        return selectedItems
            .slice()
            .sort((a, b) => allItems.indexOf(a) - allItems.indexOf(b));
    }

    const selectItem = (item: T) => {
        update((state) => {
            return clearArrowMemory({
                ...state,
                selectedItems: [item],
                lastSelectedItemNotByShiftClick: item,
            });
        });
    };

    const selectPreviousItem = (currentItem: T) => {
        update((state) => {
            const items = state.allItems;
            let item = items[items.indexOf(currentItem) - 1];
            if (item) {
                return clearArrowMemory({
                    ...state,
                    selectedItems: [item],
                    lastSelectedItemNotByShiftClick: item,
                });
            }
            return state;
        });
    };

    const selectNextItem = (currentItem: T) => {
        update((state) => {
            const items = state.allItems;
            let item = state.allItems[items.indexOf(currentItem) + 1];
            if (item) {
                return clearArrowMemory({
                    ...state,
                    selectedItems: [item],
                    lastSelectedItemNotByShiftClick: item,
                });
            }
            return state;
        });
    };

    const addOrRemoveTrack = (item: T) => {
        return update((state) => {
            let isSelected = state.selectedItems.includes(item);
            let selectedItems = isSelected
                ? state.selectedItems.filter((t) => t !== item)
                : itemsInOriginalOrder(
                      [...state.selectedItems, item],
                      state.allItems,
                  );
            return clearArrowMemory({
                ...state,
                selectedItems,
                lastSelectedItemNotByShiftClick: item,
            });
        });
    };

    const selectMultiple = (items: T[]) => {
        return update((state) => {
            let selectedItems = itemsInOriginalOrder(
                [...state.selectedItems, ...items],
                state.allItems,
            );
            return clearArrowMemory({ ...state, selectedItems });
        });
    };

    const selectAll = () => {
        return update((state) => {
            let selectedItems = state.allItems;
            return clearArrowMemory({ ...state, selectedItems });
        });
    };

    const selectUntil = (item: T) => {
        return update((state) => {
            const allItems = state.allItems;
            let lastNonShiftSelectedTrackIndex = allItems.findIndex(
                (track) => track === state.lastSelectedItemNotByShiftClick,
            );
            if (lastNonShiftSelectedTrackIndex === undefined) {
                selectItem(item);
                return;
            }
            let newIndex = allItems.findIndex((track) => track === item);
            let itemIsAlreadySelected = state.selectedItems.includes(item);
            let itemsInRange: T[];
            if (newIndex > lastNonShiftSelectedTrackIndex) {
                itemsInRange = allItems.slice(
                    lastNonShiftSelectedTrackIndex,
                    newIndex + 1,
                );
            } else {
                itemsInRange = allItems.slice(
                    newIndex,
                    lastNonShiftSelectedTrackIndex + 1,
                );
            }
            let newSelection = [...state.selectedItems];
            if (itemIsAlreadySelected) {
                newSelection = newSelection.filter((t) =>
                    itemsInRange.includes(t),
                );
            } else {
                itemsInRange.forEach((t) => {
                    if (!newSelection.includes(t)) newSelection.push(t);
                });
                newSelection = itemsInOriginalOrder(
                    newSelection,
                    state.allItems,
                );
            }
            return clearArrowMemory({ ...state, selectedItems: newSelection });
        });
    };

    const clearSelection = () => {
        update((state) => {
            return clearArrowMemory({ ...state, selectedItems: [] });
        });
    };

    return {
        subscribe,
        set,
        update,
        selectItem,
        selectPreviousItem,
        selectNextItem,
        selectMultiple,
        selectAll,
        clearSelection,
        setCurrentMouseTarget: (item) => {
            update((state) => ({ ...state, currentMouseTarget: item }));
        },
        /**
         * Since a page can have several track lists, this method is called only once each track list on the page.
         * Each track list passes its list of items to this component so it knows about all of them, since it must
         * allow to select items or ranges of items that spawn across track lists.
         * @param newItems
         */
        addItems: (identifier, newItems) => {
            update((state) => {
                const itemLists = state.itemLists;
                const currentListItems = itemLists.get(identifier);
                const items = [...(currentListItems || []), ...newItems];
                itemLists.set(identifier, items);
                const allItems = flattenItemLists(itemLists);

                // Initialized to the first track marked as prominent. It is not reactive to allow
                // users to highlight other tracks using mouse or keyboard.  Looks in allItems
                // array as a multi-disc album will render multiple sets of track lists.  This ensures
                // that as additional track sets are loaded, the prominent track remains highlighted.
                let firstProminentItem = allItems.find(
                    (item) => 'isProminent' in item && item.isProminent,
                );
                let selectedItems =
                    state.selectedItems.length > 0
                        ? state.selectedItems
                        : firstProminentItem
                        ? [firstProminentItem]
                        : [];
                return { ...state, itemLists, allItems, selectedItems };
            });
        },
        getItems: (identifier) => {
            return get(itemSelectorState).itemLists.get(identifier) || [];
        },
        /**
         * This handles all the possible actions than can happen when clicking on a track, from a simple selection/unselection
         * to multiple selection or ranges.
         * @param item
         * @param e MouseEvent
         */
        handleClick: (item, e) => {
            if (e.shiftKey) {
                selectUntil(item);
            } else if (e.metaKey) {
                addOrRemoveTrack(item);
            } else {
                selectItem(item);
            }
        },
        /**
         * Handles keyboard selection using arrow keys, including multiple selection.
         * @param e KeyboardEvent
         */
        handleKeydown: (e) => {
            update((state) => {
                const allItems = state.allItems;
                let currentIndex = allItems.findIndex(
                    (track) => track === state.lastSelectedItemNotByShiftClick,
                );
                // rdar://94457690 (JMOTW: Investigate focus managements on track lists) -> We might want to handle Tab/shift+tab here.
                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault();
                        if (e.shiftKey) {
                            if (
                                !state.shiftArrowDirection ||
                                state.shiftArrowDirection ===
                                    SelectionDirection.down
                            ) {
                                state.shiftArrowDirection =
                                    SelectionDirection.down;
                                expandShiftArrowSelection(state);
                            } else {
                                contractShiftArrowSelection(state);
                            }
                            return { ...state };
                        } else {
                            let nextItem = allItems[currentIndex + 1];
                            if (nextItem) {
                                state.lastSelectedItemNotByShiftClick =
                                    nextItem;
                                state.selectedItems = [nextItem];
                            }
                            return clearArrowMemory(state);
                        }
                    case 'ArrowUp':
                        e.preventDefault();
                        if (e.shiftKey) {
                            if (
                                !state.shiftArrowDirection ||
                                state.shiftArrowDirection ===
                                    SelectionDirection.up
                            ) {
                                state.shiftArrowDirection =
                                    SelectionDirection.up;
                                expandShiftArrowSelection(state);
                            } else {
                                contractShiftArrowSelection(state);
                            }
                            return { ...state };
                        } else {
                            let previousItem = allItems[currentIndex - 1];
                            if (previousItem) {
                                state.lastSelectedItemNotByShiftClick =
                                    previousItem;
                                state.selectedItems = [previousItem];
                            }
                            return clearArrowMemory(state);
                        }
                    default:
                        return state;
                }
            });
        },

        /**
         * This method must be invoked when the track list using this store is destroyed, so all the state
         * is cleaned and the next time another track list is rendered it doesn't contain items from previous
         * track lists.
         */
        resetItems: (identifier) => {
            update((state) => {
                const itemLists = state.itemLists;
                itemLists.delete(identifier);
                const allItems = flattenItemLists(itemLists);
                return {
                    itemLists,
                    allItems,
                    currentMouseTarget: null,
                    selectedItemsWithShiftArrows: [],
                    selectedItems: [],
                    shiftArrowDirection: undefined,
                    lastSelectedItemNotByShiftClick: undefined,
                };
            });
        },
    };
}

const CONTEXT_NAME = 'itemSelector';

export function setupItemSelector(context: Map<string, unknown>) {
    const itemSelector = createItemSelector<ItemSelectorItem>();
    context.set(CONTEXT_NAME, itemSelector);
}

export function getItemSelector(): ItemSelector<ItemSelectorItem> {
    const ItemSelectorInstance = getContext(CONTEXT_NAME) as
        | ItemSelector<ItemSelectorItem>
        | undefined;
    if (!ItemSelectorInstance) {
        throw new Error('getItemSelector called before setupItemSelector');
    }
    return ItemSelectorInstance;
}
